import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, TrashIcon, UserGroupIcon, ShieldCheckIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { fetchUsers, createUser, updateUser, deleteUser, fetchRoles } from '../../store/slices/adminSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, Badge, SearchInput, Pagination, ConfirmDialog, Card, Modal, Input, Select } from '../../components/Common';
import api from '../../services/api';

const UserManagement = () => {
  const dispatch = useDispatch();
  const { users, loading, pagination, roles } = useSelector((state) => state.admin);
  const { user: currentUser } = useSelector((state) => state.auth);
  const [stats, setStats] = useState({ totalUsers: 0, totalAdmins: 0, totalOperators: 0, activeUsers: 0 });
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, type: null, name: '' });
  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', middle_name: '', last_name: '',
    phone_number: '', role_type_id: '4', two_factor_required: false
  });
  const [adminFormData, setAdminFormData] = useState({
    email: '', password: '', first_name: '', middle_name: '', last_name: '', two_factor_required: false
  });
  const [submitting, setSubmitting] = useState(false);

  // Check if current user has admin permissions
  const hasAdminPermission = () => {
    if (!currentUser) return false;
    const perms = currentUser.permissions;
    if (perms === 'ALL' || (Array.isArray(perms) && perms.includes('ALL'))) return true;
    if (Array.isArray(perms)) return perms.includes('CREATE_ADMIN') || perms.includes('DELETE_ADMIN');
    if (typeof perms === 'string') {
      const permList = perms.split(',').map(p => p.trim());
      return permList.includes('ALL') || permList.includes('CREATE_ADMIN') || permList.includes('DELETE_ADMIN');
    }
    return currentUser.type === 'admin';
  };

  useEffect(() => {
    dispatch(fetchUsers({ page, size: pageSize, search, role_type_id: roleFilter, type: typeFilter }));
    dispatch(fetchRoles());
  }, [dispatch, page, pageSize, search, roleFilter, typeFilter]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await api.get('/admin/dashboard');
        if (res.data.success) {
          const overview = res.data.data.overview || {};
          setStats({
            totalUsers: overview.totalUsers || 0,
            totalAdmins: overview.totalAdmins || 0,
            totalOperators: (overview.totalUsers || 0) - (overview.totalAdmins || 0),
            activeUsers: overview.totalUsers || 0
          });
        }
      } catch (err) {
        console.error('Failed to load user stats:', err);
      }
    };
    loadStats();
  }, []);

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email || '', password: '', first_name: user.firstName || '',
        middle_name: user.middleName || '', last_name: user.lastName || '',
        phone_number: user.phone || '', role_type_id: user.primaryRole?.id?.toString() || '4',
        two_factor_required: user.twoFactorRequired || false
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '', password: '', first_name: '', middle_name: '', last_name: '',
        phone_number: '', role_type_id: '4', two_factor_required: false
      });
    }
    setModalOpen(true);
  };

  const openAdminModal = () => {
    setAdminFormData({
      email: '', password: '', first_name: '', middle_name: '', last_name: '', two_factor_required: false
    });
    setAdminModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) return;
    if (!editingUser && !formData.password.trim()) return;

    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (editingUser && !submitData.password) delete submitData.password;

      if (editingUser) {
        await dispatch(updateUser({ id: editingUser.id, data: submitData })).unwrap();
        dispatch(showSuccess('User updated successfully'));
      } else {
        await dispatch(createUser(submitData)).unwrap();
        dispatch(showSuccess('User created successfully'));
      }
      setModalOpen(false);
      dispatch(fetchUsers({ page, size: pageSize, search, role_type_id: roleFilter, type: typeFilter }));
    } catch (error) {
      console.error('Submit error:', error);
      dispatch(showError(error || 'Failed to save user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (!adminFormData.email.trim() || !adminFormData.password.trim() || !adminFormData.first_name.trim()) return;

    setSubmitting(true);
    try {
      // Find ADMIN role ID from roles list
      const adminRole = (roles || []).find(r => r.code === 'ADMIN');
      const adminRoleId = adminRole?.id || 1;
      
      // Create user with ADMIN role using unified createUser
      await dispatch(createUser({
        ...adminFormData,
        role_type_id: adminRoleId.toString()
      })).unwrap();
      dispatch(showSuccess('Admin user created successfully'));
      setAdminModalOpen(false);
      dispatch(fetchUsers({ page, size: pageSize, search, role_type_id: roleFilter, type: typeFilter }));
    } catch (error) {
      console.error('Admin submit error:', error);
      dispatch(showError(error || 'Failed to create admin user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        // Use unified deleteUser for all users (including admins)
        await dispatch(deleteUser({ id: deleteModal.id })).unwrap();
        dispatch(showSuccess(deleteModal.type === 'admin' ? 'Admin user deleted successfully' : 'User deleted successfully'));
        dispatch(fetchUsers({ page, size: pageSize, search, role_type_id: roleFilter, type: typeFilter }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete user'));
      }
      setDeleteModal({ open: false, id: null, type: null, name: '' });
    }
  };

  // Get role name from roles list by ID
  const getRoleNameById = (roleId) => {
    if (!roleId) return null;
    const role = (roles || []).find(r => r.id === roleId || r.id === parseInt(roleId));
    return role ? role.name : null;
  };

  const getRoleBadge = (role, row) => {
    const roleColors = {
      'ADMIN': 'danger',
      'SUPER_ADMIN': 'danger',
      'CONTENT_MANAGER': 'warning',
      'EDITOR': 'info',
      'CONTENT_WRITER': 'primary',
      'WRITER': 'primary',
      'VIEWER': 'secondary',
    };
    
    // Try to get role name from primaryRole object
    let roleName = role?.name;
    let roleCode = role?.code;
    
    // If no role name, try to find from roles list using role ID
    if (!roleName && role?.id) {
      roleName = getRoleNameById(role.id);
    }
    
    // If still no role name, check if row has roles array
    if (!roleName && row?.roles && row.roles.length > 0) {
      roleName = row.roles[0]?.name;
      roleCode = row.roles[0]?.code;
    }
    
    return <Badge variant={roleColors[roleCode] || 'primary'}>{roleName || 'No Role'}</Badge>;
  };

  const roleOptions = (roles || []).map(role => ({ value: role.id.toString(), label: role.name }));

  // Filter users by type (admin/operator based on role)
  const filteredUsers = typeFilter 
    ? users.filter(u => u.type === typeFilter)
    : users;

  const columns = [
    {
      key: 'name',
      title: 'User',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${row.type === 'admin' ? 'bg-gradient-to-r from-red-500 to-pink-500' : 'bg-gradient-primary'}`}>
            {(row.firstName?.charAt(0) || row.email?.charAt(0) || '?').toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-slate-800">{`${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email}</p>
            <p className="text-sm text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'primaryRole',
      title: 'Role',
      render: (value, row) => getRoleBadge(value, row),
    },
    {
      key: 'type',
      title: 'Type',
      render: (value) => (
        <Badge variant={value === 'admin' ? 'danger' : 'info'}>
          {value === 'admin' ? '👑 Admin' : '👤 Operator'}
        </Badge>
      ),
    },
    {
      key: 'twoFactorRequired',
      title: '2FA',
      render: (value) => (
        <div className={`flex items-center gap-1 ${value ? 'text-emerald-600' : 'text-slate-400'}`}>
          <ShieldCheckIcon className="w-4 h-4" />
          <span className="text-sm">{value ? 'Enabled' : 'Disabled'}</span>
        </div>
      ),
    },
    {
      key: 'accountLocked',
      title: 'Status',
      render: (value) => (
        <Badge variant={value ? 'danger' : 'success'} dot>
          {value ? 'Locked' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openModal(row)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit">
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </button>
          {(row.type !== 'admin' || hasAdminPermission()) && row.id !== currentUser?.id && row.id !== currentUser?.operatorId && (
            <button 
              onClick={() => setDeleteModal({ open: true, id: row.id, type: row.type, name: `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email })} 
              className="p-2 hover:bg-red-50 rounded-lg" 
              title="Delete"
            >
              <TrashIcon className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-slate-500">Manage system users and admin accounts (unified operator system)</p>
        </div>
        <div className="flex gap-2">
          {hasAdminPermission() && (
            <Button variant="danger" icon={UserPlusIcon} onClick={openAdminModal}>
              New Admin
            </Button>
          )}
          <Button icon={PlusIcon} onClick={() => openModal()}>New User</Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm">Total Users</p>
              <p className="text-3xl font-bold">{stats.totalUsers}</p>
            </div>
            <UserGroupIcon className="w-12 h-12 text-indigo-200 opacity-80" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Admin Users</p>
              <p className="text-3xl font-bold">{stats.totalAdmins}</p>
            </div>
            <ShieldCheckIcon className="w-12 h-12 text-red-200 opacity-80" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Operators</p>
              <p className="text-3xl font-bold">{stats.totalOperators}</p>
            </div>
            <UserGroupIcon className="w-12 h-12 text-emerald-200 opacity-80" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm">Active Users</p>
              <p className="text-3xl font-bold">{stats.activeUsers}</p>
            </div>
            <ShieldCheckIcon className="w-12 h-12 text-amber-200 opacity-80" />
          </div>
        </div>
      </div>

      <Card noPadding>
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-center gap-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search users..." className="flex-1 max-w-md" />
          <Select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            options={[
              { value: '', label: 'All Types' },
              { value: 'admin', label: '👑 Admin Users' },
              { value: 'operator', label: '👤 Operators' }
            ]}
            className="w-40"
          />
          <Select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'All Roles' }, ...roleOptions]}
            className="w-40"
          />
        </div>

        <Table columns={columns} data={filteredUsers} loading={loading} emptyMessage="No users found" emptyIcon={UserGroupIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* User Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingUser ? 'Edit User' : 'New User'} size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting}>{editingUser ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} placeholder="user@example.com" required disabled={!!editingUser} />
          {!editingUser && (
            <Input label="Password" type="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} placeholder="••••••••" required />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input label="First Name" value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} placeholder="John" required />
            <Input label="Last Name" value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} placeholder="Doe" />
          </div>
          <Input label="Phone Number" value={formData.phone_number} onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))} placeholder="+1234567890" />
          <Select label="Role" value={formData.role_type_id} onChange={(e) => setFormData(prev => ({ ...prev, role_type_id: e.target.value }))} options={roleOptions} placeholder="Select role" />
          <div className="flex items-center gap-3">
            <input type="checkbox" id="2fa" checked={formData.two_factor_required} onChange={(e) => setFormData(prev => ({ ...prev, two_factor_required: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-indigo-600" />
            <label htmlFor="2fa" className="text-sm text-slate-700">Require Two-Factor Authentication</label>
          </div>
        </form>
      </Modal>

      {/* Admin User Modal */}
      <Modal isOpen={adminModalOpen} onClose={() => setAdminModalOpen(false)} title="Create New Admin User" size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdminModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleAdminSubmit} loading={submitting}>Create Admin</Button>
          </>
        }
      >
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            ⚠️ Admin users have full system access including the ability to create and delete other admin accounts.
          </p>
        </div>
        <form onSubmit={handleAdminSubmit} className="space-y-4">
          <Input label="Email" type="email" value={adminFormData.email} onChange={(e) => setAdminFormData(prev => ({ ...prev, email: e.target.value }))} placeholder="admin@example.com" required />
          <Input label="Password" type="password" value={adminFormData.password} onChange={(e) => setAdminFormData(prev => ({ ...prev, password: e.target.value }))} placeholder="••••••••" required helperText="Minimum 8 characters with uppercase, lowercase, and numbers" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="First Name" value={adminFormData.first_name} onChange={(e) => setAdminFormData(prev => ({ ...prev, first_name: e.target.value }))} placeholder="Admin" required />
            <Input label="Last Name" value={adminFormData.last_name} onChange={(e) => setAdminFormData(prev => ({ ...prev, last_name: e.target.value }))} placeholder="User" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="admin2fa" checked={adminFormData.two_factor_required} onChange={(e) => setAdminFormData(prev => ({ ...prev, two_factor_required: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-red-600" />
            <label htmlFor="admin2fa" className="text-sm text-slate-700">Require Two-Factor Authentication</label>
          </div>
        </form>
      </Modal>

      <ConfirmDialog 
        isOpen={deleteModal.open} 
        onClose={() => setDeleteModal({ open: false, id: null, type: null, name: '' })} 
        onConfirm={handleDelete} 
        title={deleteModal.type === 'admin' ? 'Delete Admin User' : 'Delete User'} 
        message={
          deleteModal.type === 'admin' 
            ? `Are you sure you want to delete admin user "${deleteModal.name}"? This will permanently remove their admin access.`
            : `Are you sure you want to delete user "${deleteModal.name}"? This action cannot be undone.`
        } 
        type="danger" 
        confirmText="Delete" 
      />
    </div>
  );
};

export default UserManagement;
